import { useState, useMemo, useCallback, useEffect, useRef } from 'react'
import throttle from 'lodash/throttle'

import { Resume } from '@rio/types'
import { PreviewRefProps } from 'builder/components/PreviewPanel'
import templateHeightFactorSelector from 'builder/components/Helper/utils/templateHeightFactorSelector'

const SCREEN_RESIZE_THROTTLE_DELAY = 150

export const usePreviewResume = (resume: Resume) => {
  const previewRef = useRef<PreviewRefProps>(null)
  const previewBoxRef = useRef<HTMLDivElement | null>(null)
  const contentRef = useRef<HTMLDivElement | null>(null)
  const [currentPageNumber, setCurrentPageNumber] = useState<number>(1)
  const [totalPageNumber, setTotalPageNumber] = useState<number>(0)
  const hasPreviousPage = useMemo(() => currentPageNumber !== 1, [currentPageNumber])
  const [dimensions, setDimensions] = useState<{ width: number; height: number }>({
    width: 0,
    height: 0,
  })
  const pageFormatFactor = templateHeightFactorSelector(resume)

  const hasNextPage = useMemo(
    () => currentPageNumber < totalPageNumber,
    [currentPageNumber, totalPageNumber],
  )

  const handlePreviousPageClick = useCallback(() => {
    if (hasPreviousPage) {
      previewRef.current?.previousPage()
      setCurrentPageNumber(currentPageNumber - 1)
    }
  }, [currentPageNumber, hasPreviousPage, previewRef])

  const handleNextPageClick = useCallback(() => {
    if (hasNextPage) {
      previewRef.current?.nextPage()
      setCurrentPageNumber(currentPageNumber + 1)
    }
  }, [currentPageNumber, hasNextPage, previewRef])

  const handlePaginationStateChange = useCallback(
    (state: { currentPage: number; totalPagesCount: number }) => {
      setCurrentPageNumber(state.currentPage + 1)
      setTotalPageNumber(state.totalPagesCount)
    },
    [],
  )

  const handlePreviewResize = useCallback(() => {
    if (!previewBoxRef.current) return

    const width = previewBoxRef.current?.offsetWidth
    const height = width / pageFormatFactor
    setDimensions({ width, height })

    if (previewRef.current) {
      previewRef.current?.resize(width)
    }
  }, [previewBoxRef, previewRef, pageFormatFactor])

  useEffect(() => {
    const handleResizeThrottled = throttle(handlePreviewResize, SCREEN_RESIZE_THROTTLE_DELAY)

    window.addEventListener('resize', handleResizeThrottled)
    handleResizeThrottled()

    return () => {
      window.removeEventListener('resize', handleResizeThrottled)
    }
  }, [contentRef, handlePreviewResize])

  return {
    handlePreviousPageClick,
    handleNextPageClick,
    handlePaginationStateChange,
    handlePreviewResize,
    hasPreviousPage,
    hasNextPage,
    totalPageNumber,
    currentPageNumber,
    dimensions,
    previewRef,
    previewBoxRef,
    contentRef,
  }
}
